import {
  Dispatch,
  FC,
  JSX,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useRouter } from "next/router";
import { Heading, Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { Trans } from "@lingui/macro";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { Locale } from "src/lib/Intl";
import { stringifyUrl } from "query-string";
import {
  BrandGateLevelInCustomerPlatform,
  BrandLabel,
  HomePageSingleBrand,
  HomePageSingleBrandLabel,
  ImageBannerData,
  PromotionDisplayType,
  SummaryBrand,
  WishlistItemType,
} from "src/types/graphql.d";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import { Ratio } from "src/atoms/Ratio";
import { Skeleton } from "src/atoms/Skeleton";
import { Label } from "src/atoms/Label";
import {
  StyledBrandCard,
  StyledBrandCardImageWrapper,
  StyledBrandCardInfo,
  StyledBrandCardInfoLabelWrapper,
  StyledBrandCardInfoText,
  StyledBrandCardLineClamp,
  StyledBrandCardLogo,
  StyledGatedBrandCard,
} from "./BrandCard.styled";
import { BrandCardImage } from "./BrandCardImage";
import { ArrowNext } from "src/icons/ArrowNext";
import { ToggleWishListButton } from "src/molecules/ToggleWishListButton";
import { useDeleteWishlistItem } from "src/hooks/deleteWishlistItem";
import { useAddToWishList } from "src/hooks/useAddToWishlist";
import { useImageError } from "src/hooks/useImageError";
import { formatName } from "src/utils/formatName";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useSegment } from "src/hooks/useSegment";
import { useToggleWishlistBrandItem } from "src/hooks/toggleWishlistItem";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import {
  SEGMENT_PROMOTION_TYPE,
  SEGMENT_LIST_FORMAT,
  SEGMENT_LIST_TYPE,
} from "src/segment";
import NoSSR from "src/atoms/NoSSR";
import { Timer } from "src/atoms/Timer";
import { LockIcon, LockUnlockIcon } from "@otrium/icons";
import { LoginRegisterModal } from "src/molecules/LoginRegisterModal";
import {
  GATED_BRAND_CAROUSEL_CARD_SOURCE,
  GATED_BRAND_CAROUSEL_FAVOURITE_SOURCE,
  GATED_POPUP_REDIRECT_DATA,
} from "src/constants/gatedItem";
import {
  deleteSessionStorageItem,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { theme } from "src/theme";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import classNames from "classnames";

interface BrandCardLabelsProps {
  labels: BrandLabel[] | HomePageSingleBrandLabel[];
}

const BrandCardLabels: FC<BrandCardLabelsProps> = ({ labels }): JSX.Element => {
  const firstTwoLabels = labels?.slice(0, 2);
  return (
    <div>
      {(firstTwoLabels as Array<BrandLabel | HomePageSingleBrandLabel>).map(
        (label: BrandLabel | HomePageSingleBrandLabel, index: number) => (
          <StyledBrandCardInfoLabelWrapper
            key={`StyledBrandCardInfoLabelWrapper-${index}`}
          >
            <Label
              type={label.type}
              value={label.value}
              color={label.color}
              textColor={label.text_color}
              showBackground={false}
            />
          </StyledBrandCardInfoLabelWrapper>
        )
      )}
    </div>
  );
};

// Card aspect ratios, expressed as height / width (consumed by <Ratio />).
// Per-device so desktop and mobile can be tuned independently.
const DEFAULT_CARD_RATIO = { mobile: 0.72, desktop: 0.72 };
const MARKETING_CARD_RATIO = { mobile: 0.67, desktop: 0.65 };

interface Props {
  associatedCategoryId?: string | null;
  brand: SummaryBrand | HomePageSingleBrand | ImageBannerData;
  genderId: string;
  /**
   * Overrides the card aspect ratio (height / width) per device. When omitted,
   * falls back to the marketing-block ratio for image banners, otherwise the
   * default card ratio.
   */
  ratio?: { mobile: number; desktop: number };
  getImageElementWrapper?: Dispatch<SetStateAction<HTMLDivElement | null>>;
  locale: Locale;
  minimal?: boolean;
  onClickHandler?: () => void;
  orderByNew?: boolean;
  refetchQueriesOnDeleteWishListBrand?: string[];
  disableTranslation?: boolean;
  brandType?: string;
  position?: string;
  tab?: string;
  handleTimerExpired?: (link: string) => void;
  pageName?: string;
  shopType?: string;
  setHasTimerEnded?: (hasTimerEnded: boolean) => void;
}

const BrandCard: FC<PropsWithChildren<Props>> = ({
  brand,
  genderId,
  locale,
  ratio,
  orderByNew = false,
  minimal = false,
  refetchQueriesOnDeleteWishListBrand = [],
  associatedCategoryId,
  getImageElementWrapper,
  onClickHandler,
  disableTranslation = false,
  brandType,
  position,
  tab,
  handleTimerExpired,
  pageName = "homepage",
  shopType = "women",
  setHasTimerEnded,
  ...props
}) => {
  const {
    id: brandId,
    overlay_logo,
    logo_svg,
    subtitle,
    slug,
    labels,
    discount,
    name: brandName,
  } = brand as SummaryBrand | HomePageSingleBrand;

  const { gate } = brand as HomePageSingleBrand;

  const {
    block_uuid,
    link,
    title,
    title_color,
    subtitle_color,
    image_mobile,
    image_desktop,
    display_type,
    end_date,
  } = brand as ImageBannerData;
  const { asPath } = useRouter();
  const [setLogoImg, hasLogoImgError] = useImageError();
  const [setBackgroundImg, hasBackgroundImgError] = useImageError();
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const isLoggedIn = useIsLoggedIn();
  const { isMobile } = useBreakpoints();
  const [hasSaleExpired, setSaleExpired] = useState(false);
  const [actionSource, setActionSource] = useState<string>(
    GATED_BRAND_CAROUSEL_CARD_SOURCE
  );

  const { enableGatedHomepage } = useFeatureFlags();

  const isGated =
    enableGatedHomepage &&
    !isLoggedIn &&
    gate &&
    gate.level !== BrandGateLevelInCustomerPlatform.NonGated;

  const [setBackgroundMinimalImg, hasBackgroundMinimalImgError] =
    useImageError();

  const { brandFavourited, brandUnfavourited, brandFavouritedWhenLoggedOut } =
    useSegment();

  const { addedToWishlist } = useToggleWishlistBrandItem(brandId);

  const { deleteWishListItem, loading: deleteLoading } = useDeleteWishlistItem({
    refetchQueries: refetchQueriesOnDeleteWishListBrand,
  });

  const { addToWishlist, loading: addLoading } = useAddToWishList({
    refetchQueries: ["wishlistBrands", "userWishlistMetaData"],
  });

  const brandWishlistTracking = {
    brandName,
    brandId,
  };

  const hasSubtitleOrDiscount = !!(subtitle || discount);

  const onAddToWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      if (isLoggedIn) {
        await addToWishlist(itemType, itemId);
        void brandFavourited(brandWishlistTracking);
        return;
      } else if (enableGatedHomepage) {
        setOpen(true);
        setActionSource(GATED_BRAND_CAROUSEL_FAVOURITE_SOURCE);
      }
      void brandFavouritedWhenLoggedOut(brandWishlistTracking);
    },
    [addToWishlist, isLoggedIn] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onRemoveFromWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      await deleteWishListItem(itemType, itemId, {});
      void brandUnfavourited(brandWishlistTracking);
    },
    [deleteWishListItem] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onTimerExpire = useCallback(
    (hasTimerEnded: boolean) => {
      if (!hasTimerEnded || !handleTimerExpired || !block_uuid) {
        return;
      }

      handleTimerExpired(block_uuid);
    },
    [handleTimerExpired, block_uuid]
  );

  const cardImage: string | undefined = useMemo(() => {
    const defaultImage = "image" in brand ? (brand.image as string) : undefined;
    const oldCoverImages = isMobile
      ? image_mobile || defaultImage
      : image_desktop || defaultImage;

    if (
      !("images" in brand) ||
      !brand.images[genderId as Exclude<ShopType, ShopType.All>]
    ) {
      return oldCoverImages;
    }

    return "images" in brand
      ? brand.images[genderId as Exclude<ShopType, ShopType.All>].find(
          ({ device }) => device === (isMobile ? "mobile" : "desktop")
        )?.url
      : oldCoverImages;
  }, [brand, image_desktop, image_mobile, genderId, isMobile]);

  const orderBy = orderByNew ? "new" : undefined;

  const singleBrand = brand as SummaryBrand | HomePageSingleBrand;

  const brandGenders = singleBrand?.genders;

  const hasSale =
    (display_type &&
      (display_type as PromotionDisplayType) !==
        PromotionDisplayType.Default) ||
    false;

  const areGendersAvailable =
    Array.isArray(brandGenders) && brandGenders.length > 0;

  const getGender =
    areGendersAvailable && !brandGenders?.includes(genderId)
      ? brandGenders?.[0]
      : genderId;

  const routePath = `${getI18nRoute({
    route: `/brands`,
    locale,
  })}/${slug}${getI18nRoute({
    route: `/${localeFix({ gender: getGender })}`,
    locale,
  })}`;
  const isMarketingBlock = brandType === SEGMENT_PROMOTION_TYPE.imageBanners;

  const path =
    link ||
    stringifyUrl({
      url: routePath,
      query: { orderBy, cato_cat: associatedCategoryId || undefined },
    });

  const ratioByDevice =
    ratio ?? (isMarketingBlock ? MARKETING_CARD_RATIO : DEFAULT_CARD_RATIO);
  const cardRatio = isMobile ? ratioByDevice.mobile : ratioByDevice.desktop;

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setOpen(true);
    },
    [setOpen]
  );

  const handleMouseEnter = useCallback(() => {
    if (isMobile) {
      return;
    }

    setHovered(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) {
      return;
    }

    setHovered(false);
  }, [isMobile]);

  const setRedirectData = (type: string) => {
    deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
    setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, {
      gatedActionType: type,
      promo_id: brandId,
      promo_name: `${brandName}-${discount || ""}`,
      promo_creative: cardImage,
      promo_position: position,
      promo_type: SEGMENT_PROMOTION_TYPE.brandCarousel as string,
      promo_path: asPath,
      productListMeta: {
        listFormat: SEGMENT_LIST_FORMAT.carousel,
        listId: slug,
        listType: SEGMENT_LIST_TYPE.brand,
        shopType,
      },
      tab,
      actionSource,
    });
  };

  const handleOnTimerExpire = useCallback(() => {
    return (hasTimerEnded: boolean) => {
      if (!hasTimerEnded) return;
      if (setHasTimerEnded) setHasTimerEnded(true);

      if (
        display_type === PromotionDisplayType.LightningDeal ||
        display_type === PromotionDisplayType.FlashSale
      ) {
        onTimerExpire(true);
      } else {
        setSaleExpired(true);
      }
    };
  }, [display_type, onTimerExpire, setHasTimerEnded]);

  // render the minimal version
  if (minimal) {
    return (
      <StyledBrandCard
        {...props}
        className={classNames({
          "has-sale-expired": hasSale && hasSaleExpired,
        })}
        aria-disabled={hasSale && hasSaleExpired}
        tabIndex={hasSale && hasSaleExpired ? -1 : undefined}
        href={path}
        prefetch={false}
      >
        {brandId && (
          <ToggleWishListButton
            itemType={WishlistItemType.Brand}
            itemId={brandId}
            loading={deleteLoading || addLoading}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onAddToWishList={onAddToWishList}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onRemoveWishList={onRemoveFromWishList}
            isAddedToWishList={addedToWishlist}
          />
        )}
        <Ratio ratio={1.32}>
          <StyledBrandCardImageWrapper isBottomRadius={true}>
            {!hasBackgroundMinimalImgError && cardImage && (
              <BrandCardImage
                ref={setBackgroundMinimalImg}
                src={cardImage}
                alt={brandName}
                isBottomRadius={true}
              />
            )}
          </StyledBrandCardImageWrapper>
        </Ratio>
        <Box
          pb={3}
          width={1}
          sx={{
            "&::after": {
              backgroundImage: isMarketingBlock
                ? null
                : "linear-gradient(to bottom,rgba(0,0,0,0),#000000)",
              content: "''",
              height: "150%",
              width: "100%",
              position: "absolute",
              bottom: 0,
              opacity: 0.7,
              zIndex: 0,
            },
            position: "absolute",
            bottom: 0,
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {brandName && (
              <Text
                color="tone.white"
                textAlign={"center"}
                fontSize={2}
                pb={0}
                sx={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                }}
              >
                {formatName(brandName)}
              </Text>
            )}
            {brand.subtitle && (
              <Text
                fontSize={1}
                color={subtitle_color || "tone.white"}
                textAlign={"center"}
              >
                {brand.subtitle}
              </Text>
            )}
          </Box>
        </Box>
      </StyledBrandCard>
    );
  }

  const renderLogoOrText = () =>
    !hasLogoImgError && (logo_svg || overlay_logo) ? (
      <StyledBrandCardLogo
        ref={setLogoImg}
        src={logo_svg || (overlay_logo as string)}
        alt={brandName}
      />
    ) : (
      <Flex
        fontSize={3}
        sx={{
          position: "absolute",
          zIndex: "11",
          top: "75%",
          left: "50%",
          color: "white",
          fontWeight: "bold",
          bottom: 0,
          textTransform: "uppercase",
          textShadow: "1px #000",
          textAlign: "center",
          transform: "translate(-50%, -50%)",
          width: "100%",
        }}
        alignItems="center"
      >
        <Text>{brandName}</Text>
      </Flex>
    );

  const renderImage = () =>
    !hasBackgroundImgError && cardImage ? (
      <BrandCardImage
        ref={setBackgroundImg}
        src={cardImage}
        alt={brandName}
        noRadius={isMarketingBlock}
        isBottomRadius={!hasSubtitleOrDiscount}
        hovered={hovered}
        isGated={isGated || false}
        isMobile={isMobile}
      />
    ) : null;

  const renderFooter = () => (
    <Flex
      alignItems="center"
      bg="tone.white"
      width={1}
      px={"space16"}
      sx={{
        height: isMarketingBlock ? "auto" : 60,
        border: isMarketingBlock ? "none" : "1px solid #eeeeee",
        borderRadius: isMarketingBlock ? "none" : "0 0 8px 8px",
        justifyContent: "space-between",
        flexFlow: "row wrap",
        position: "relative",
        zIndex: 11,
        borderTop: "none",
        flex: "1 1 auto",
      }}
    >
      <StyledBrandCardInfoText>
        <Flex flexDirection="column" flex="1 0">
          <Flex alignItems="center">
            {hovered ? (
              <LockUnlockIcon
                width={20}
                height={20}
                color={theme.colors.tone.black}
                style={{ margin: "0 6px" }}
              />
            ) : (
              <LockIcon
                color={theme.colors.tone.black}
                width={32}
                height={32}
                style={{
                  background: theme.colors.primary.goldLight,
                  borderRadius: "16px",
                  padding: "6px",
                }}
              />
            )}
            {title && (
              <Heading variant="h2" color={title_color}>
                <StyledBrandCardLineClamp>{title}</StyledBrandCardLineClamp>
              </Heading>
            )}
            <Text
              fontSize={"fontSize16"}
              sx={{
                lineHeight: "24px",
                fontWeight: "600",
                paddingLeft: "8px",
                color: subtitle_color || "tone.black",
                ":first-letter": {
                  textTransform: subtitle ? "none" : "capitalize",
                },
              }}
            >
              <StyledBrandCardLineClamp>
                <Trans>Log in to access</Trans>
              </StyledBrandCardLineClamp>
            </Text>
          </Flex>
        </Flex>
        <NoSSR>
          <Flex
            sx={{
              marginLeft: "auto",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ArrowNext role="presentation" />
          </Flex>
        </NoSSR>
      </StyledBrandCardInfoText>
    </Flex>
  );

  return (
    <>
      {isGated ? (
        <StyledGatedBrandCard
          {...props}
          onClick={() => setOpen(true)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onContextMenu={handleContextMenu}
          className={classNames({
            "marketing-block": isMarketingBlock,
          })}
        >
          <Box
            sx={{
              position: "absolute",
              right: "16px",
              top: "16px",
              zIndex: 1,
            }}
          >
            <LockIcon width={20} height={20} color={theme.colors.tone.white} />
          </Box>
          <Ratio ratio={cardRatio}>
            {renderLogoOrText()}
            <StyledBrandCardImageWrapper
              ref={getImageElementWrapper}
              isBottomRadius={!hasSubtitleOrDiscount}
              noGradient={brandType === "image_banners"}
              noRadius={isMarketingBlock}
            >
              {renderImage()}
            </StyledBrandCardImageWrapper>
          </Ratio>
          {renderFooter()}
        </StyledGatedBrandCard>
      ) : (
        <StyledBrandCard
          {...props}
          onClick={onClickHandler}
          className={classNames({
            "marketing-block": isMarketingBlock,
            "has-sale-expired": hasSale && hasSaleExpired,
          })}
          href={path}
        >
          {brandId && (
            <ToggleWishListButton
              itemType={WishlistItemType.Brand}
              itemId={brandId}
              loading={deleteLoading || addLoading}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onAddToWishList={onAddToWishList}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onRemoveWishList={onRemoveFromWishList}
              isAddedToWishList={addedToWishlist}
            />
          )}
          <Ratio ratio={cardRatio}>
            {renderLogoOrText()}
            <StyledBrandCardImageWrapper
              ref={getImageElementWrapper}
              isBottomRadius={!hasSubtitleOrDiscount}
              noGradient={brandType === "image_banners"}
              noRadius={isMarketingBlock}
            >
              {renderImage()}
            </StyledBrandCardImageWrapper>
          </Ratio>
          {(hasSubtitleOrDiscount || title) && (
            <Flex
              alignItems="center"
              bg="tone.white"
              width={1}
              px={isMarketingBlock ? "2" : "3"}
              py={isMarketingBlock ? "3" : "2"}
              sx={{
                height: isMarketingBlock ? "auto" : 60,
                border: isMarketingBlock ? "none" : "1px solid #eeeeee",
                borderRadius: isMarketingBlock ? "none" : "0 0 8px 8px",
                justifyContent: "space-between",
                flexFlow: "row wrap",
                position: "relative",
                zIndex: 11,
                borderTop: "none",
                flex: "1 1 auto",
              }}
            >
              <StyledBrandCardInfoText>
                <Flex flexDirection="column" flex="1 0">
                  {labels && (
                    <Box sx={{ whiteSpace: "nowrap" }}>
                      <BrandCardLabels labels={labels} />
                    </Box>
                  )}
                  {title && (
                    <Heading variant="h4" as={"h2"} color={"tone.black"}>
                      <StyledBrandCardLineClamp>
                        {title}
                      </StyledBrandCardLineClamp>
                    </Heading>
                  )}
                  <Text
                    fontSize={"fontSize16"}
                    as={hasSale ? "div" : "p"}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      lineHeight: "24px",
                      fontWeight: isMarketingBlock ? "400" : "600",
                      color: subtitle_color || "tone.black",
                      ":first-letter": {
                        textTransform: subtitle ? "none" : "capitalize",
                      },
                    }}
                  >
                    {(subtitle || discount || end_date) && (
                      <>
                        <StyledBrandCardLineClamp
                          style={{
                            marginRight:
                              hasSale && subtitle ? theme.space.space8 : 0,
                          }}
                        >
                          {hasSale && hasSaleExpired ? (
                            <>
                              {(display_type as PromotionDisplayType) ===
                              PromotionDisplayType.FlashSale ? (
                                <Trans>Flash sale has ended</Trans>
                              ) : null}
                              {(display_type as PromotionDisplayType) ===
                              PromotionDisplayType.LightningDeal ? (
                                <Trans>Lightning Deal Expired</Trans>
                              ) : null}
                            </>
                          ) : disableTranslation ? (
                            subtitle || discount
                          ) : (
                            <Trans>up to {subtitle || discount} discount</Trans>
                          )}
                        </StyledBrandCardLineClamp>
                        {hasSale && !hasSaleExpired ? (
                          <NoSSR>
                            <Timer
                              endDate={end_date}
                              theme="bannertimer"
                              showLabels={false}
                              onTimerEnd={handleOnTimerExpire()}
                              timerColor={theme.colors.semantic.red}
                            />
                          </NoSSR>
                        ) : null}
                      </>
                    )}
                  </Text>
                </Flex>
              </StyledBrandCardInfoText>
            </Flex>
          )}
        </StyledBrandCard>
      )}
      {open && (
        <LoginRegisterModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          redirectTo={path}
          trackingData={{
            pageName,
            shopType,
            actionSource,
            redirectTo: path,
            brandName,
            brandId,
            brandImageUrl: cardImage,
            tab,
            level: (brand as HomePageSingleBrand).gate?.level || "non_gated",
          }}
          onRedirect={setRedirectData}
        />
      )}
    </>
  );
};

const BrandCardFallback: FC = () => {
  return (
    <StyledBrandCard href="#" prefetch={false}>
      <div>
        <Ratio ratio={DEFAULT_CARD_RATIO.desktop}>
          <Skeleton />
        </Ratio>

        <StyledBrandCardInfo>
          <StyledBrandCardInfoText>
            <Skeleton height={14} width={130} />
            <div style={{ height: 6 }} />
            <Skeleton height={16} width={130} />
          </StyledBrandCardInfoText>
          <Skeleton height={36} width={100} />
        </StyledBrandCardInfo>
      </div>
    </StyledBrandCard>
  );
};

export { BrandCard, BrandCardFallback };
